import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = "https://digitalbalm.com";

const api = axios.create({ baseURL: API });
api.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async () => {
    try {
      const res = await axios.post(`${API}/api/login`, { password });
      localStorage.setItem("token", res.data.token);
      onLogin();
    } catch { setError("Invalid password"); }
  };
  return (
    <div style={{ minHeight:"100vh", background:"#0f0f0f", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#1a1a1a", padding:"40px", borderRadius:"12px", width:"320px", border:"1px solid #333" }}>
        <h2 style={{ color:"#00ff88", margin:"0 0 8px", fontSize:"20px" }}>⚡ Crypto Agent</h2>
        <p style={{ color:"#666", margin:"0 0 24px", fontSize:"13px" }}>Dashboard Access</p>
        <input
          type="password" placeholder="Password" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          style={{ width:"100%", padding:"12px", background:"#111", border:"1px solid #333", borderRadius:"8px", color:"#fff", fontSize:"14px", boxSizing:"border-box", marginBottom:"12px" }}
        />
        {error && <p style={{ color:"#ff4444", fontSize:"13px", margin:"0 0 12px" }}>{error}</p>}
        <button onClick={submit}
          style={{ width:"100%", padding:"12px", background:"#00ff88", border:"none", borderRadius:"8px", color:"#000", fontWeight:"700", fontSize:"14px", cursor:"pointer" }}>
          Login
        </button>
      </div>
    </div>
  );
}

function Badge({ text, color }) {
  const colors = { green:"#00ff88", red:"#ff4444", yellow:"#ffaa00", gray:"#666", blue:"#4488ff" };
  return <span style={{ background: colors[color]+"22", color: colors[color], padding:"2px 8px", borderRadius:"4px", fontSize:"12px", fontWeight:"600" }}>{text}</span>;
}

function Card({ title, children, accent }) {
  return (
    <div style={{ background:"#1a1a1a", border:`1px solid ${accent||"#333"}`, borderRadius:"12px", padding:"20px", marginBottom:"16px" }}>
      {title && <h3 style={{ color:"#fff", margin:"0 0 16px", fontSize:"15px", borderBottom:"1px solid #333", paddingBottom:"10px" }}>{title}</h3>}
      {children}
    </div>
  );
}

function StatBox({ label, value, color, small }) {
  const colors = { green:"#00ff88", red:"#ff4444", yellow:"#ffaa00", white:"#fff", gray:"#888" };
  return (
    <div style={{ background:"#111", padding:"14px", borderRadius:"8px" }}>
      <div style={{ color:"#555", fontSize:"11px", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.5px" }}>{label}</div>
      <div style={{ color: colors[color]||"#fff", fontSize: small ? "14px" : "18px", fontWeight:"700" }}>{value}</div>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("token"));
  const [tab, setTab] = useState("signals");
  const [signals, setSignals] = useState([]);
  const [positions, setPositions] = useState([]);
  const [performance, setPerformance] = useState({});
  const [market, setMarket] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [emergency, setEmergency] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, p, perf, m] = await Promise.all([
        api.get("/api/signals"), api.get("/api/positions"),
        api.get("/api/performance"), api.get("/api/market")
      ]);
      setSignals(s.data); setPositions(p.data);
      setPerformance(perf.data); setMarket(m.data);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("token"); setAuthed(false); }
    }
  }, []);

  useEffect(() => { if (authed) { refresh(); const i = setInterval(refresh, 30000); return () => clearInterval(i); } }, [authed, refresh]);

  const approve = async (id) => { await api.post(`/api/approve/${id}`); refresh(); };
  const reject  = async (id) => { await api.post(`/api/reject/${id}`); refresh(); };
  const closePos = async (id, type) => { await api.post(`/api/close/${id}`, { type }); refresh(); };
  const scan = async () => { setScanning(true); await api.post("/api/scan"); setTimeout(() => { setScanning(false); refresh(); }, 3000); };
  const emergencyClose = async () => {
    if (window.confirm("Emergency close ALL positions?")) {
      await api.post("/api/emergency"); setEmergency(true); refresh();
    }
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const pending   = signals.filter(s => s.status === "pending");
  const totalInvested = positions.reduce((a, p) => a + (p.position_usd || 0), 0);
  const totalPnl      = positions.reduce((a, p) => a + (p.pnl || 0), 0);
  const totalCurrent  = totalInvested + totalPnl;
  const totalPnlPct   = totalInvested > 0 ? (totalPnl / totalInvested * 100) : 0;

  // Group by profile
  const profileSummary = positions.reduce((acc, p) => {
    const prof = p.profile || "unknown";
    if (!acc[prof]) acc[prof] = { invested: 0, pnl: 0, count: 0 };
    acc[prof].invested += (p.position_usd || 0);
    acc[prof].pnl      += (p.pnl || 0);
    acc[prof].count    += 1;
    return acc;
  }, {});

  const tabs = ["signals", "positions", "performance", "market"];
  const profileColors = { low:"#4488ff", medium:"#00ff88", high:"#ffaa00", degen:"#ff4444" };

  return (
    <div style={{ minHeight:"100vh", background:"#0f0f0f", color:"#fff", fontFamily:"system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ background:"#1a1a1a", borderBottom:"1px solid #333", padding:"12px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <span style={{ color:"#00ff88", fontWeight:"700", fontSize:"16px" }}>⚡ Crypto Agent</span>
          {pending.length > 0 && <Badge text={`${pending.length} pending`} color="yellow" />}
          {emergency && <Badge text="EMERGENCY ACTIVE" color="red" />}
        </div>
        <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
          {lastUpdate && <span style={{ color:"#555", fontSize:"11px" }}>Updated {lastUpdate}</span>}
          <button onClick={scan} disabled={scanning}
            style={{ padding:"6px 14px", background:"#00ff88", border:"none", borderRadius:"6px", color:"#000", fontWeight:"600", fontSize:"12px", cursor:"pointer", opacity: scanning ? 0.6 : 1 }}>
            {scanning ? "Scanning..." : "Scan Now"}
          </button>
          <button onClick={emergencyClose}
            style={{ padding:"6px 14px", background:"#ff4444", border:"none", borderRadius:"6px", color:"#fff", fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
            Emergency Close
          </button>
          <button onClick={() => { localStorage.removeItem("token"); setAuthed(false); }}
            style={{ padding:"6px 12px", background:"#333", border:"none", borderRadius:"6px", color:"#aaa", fontSize:"12px", cursor:"pointer" }}>
            Logout
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:"4px", padding:"16px 20px 0", borderBottom:"1px solid #222" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding:"8px 18px", background: tab===t ? "#00ff88" : "transparent", border:"none", borderRadius:"8px 8px 0 0", color: tab===t ? "#000" : "#666", fontWeight: tab===t ? "700" : "400", fontSize:"13px", cursor:"pointer", textTransform:"capitalize" }}>
            {t}{t==="signals" && pending.length > 0 ? ` (${pending.length})` : ""}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:"20px", maxWidth:"900px", margin:"0 auto" }}>

        {/* SIGNALS TAB */}
        {tab === "signals" && (
          <>
            {pending.length === 0 && <Card><p style={{ color:"#666", textAlign:"center", margin:"20px 0" }}>No pending signals. Agent is monitoring 13 coins every 90 minutes.</p></Card>}
            {signals.map(s => (
              <Card key={s.id} accent={s.status==="pending" ? "#ffaa00" : s.status==="approved" ? "#00ff88" : "#ff4444"}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"12px" }}>
                  <div>
                    <span style={{ fontSize:"18px", fontWeight:"700", color:"#fff" }}>{s.symbol}</span>
                    <span style={{ marginLeft:"10px" }}><Badge text={s.direction || "BUY"} color="green" /></span>
                    <span style={{ marginLeft:"6px" }}><Badge text={s.status} color={s.status==="pending"?"yellow":s.status==="approved"?"green":"gray"} /></span>
                  </div>
                  <span style={{ color:"#888", fontSize:"12px" }}>{s.timestamp ? new Date(s.timestamp).toLocaleString() : ""}</span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px", marginBottom:"14px" }}>
                  {[["Price", s.price ? `$${Number(s.price).toLocaleString()}` : "—"],
                    ["Score", s.score ? `${s.score}/12` : "—"],
                    ["Confidence", s.confidence ? `${s.confidence}%` : "—"],
                    ["Monthly", s.monthly_regime || "—"],
                    ["Weekly", s.weekly || "—"],
                    ["Profiles", s.profiles ? s.profiles.join(", ").toUpperCase() : "—"]
                  ].map(([k,v]) => (
                    <div key={k} style={{ background:"#111", padding:"10px", borderRadius:"8px" }}>
                      <div style={{ color:"#555", fontSize:"11px", marginBottom:"4px" }}>{k}</div>
                      <div style={{ color:"#fff", fontSize:"13px", fontWeight:"600" }}>{v}</div>
                    </div>
                  ))}
                </div>
                {s.reasoning && <p style={{ color:"#aaa", fontSize:"13px", margin:"0 0 12px", lineHeight:"1.5" }}>{s.reasoning}</p>}
                {s.risk_factors && <p style={{ color:"#ff8844", fontSize:"12px", margin:"0 0 14px" }}>⚠️ {s.risk_factors}</p>}
                {s.status === "pending" && (
                  <div style={{ display:"flex", gap:"10px" }}>
                    {(s.profiles || []).map(p => (
                      <button key={p} onClick={() => approve(s.id)}
                        style={{ padding:"8px 20px", background:"#00ff88", border:"none", borderRadius:"8px", color:"#000", fontWeight:"700", fontSize:"13px", cursor:"pointer" }}>
                        ✅ Approve [{p.toUpperCase()}]
                      </button>
                    ))}
                    <button onClick={() => reject(s.id)}
                      style={{ padding:"8px 20px", background:"#333", border:"none", borderRadius:"8px", color:"#ff4444", fontWeight:"600", fontSize:"13px", cursor:"pointer" }}>
                      ❌ Reject
                    </button>
                  </div>
                )}
              </Card>
            ))}
          </>
        )}

        {/* POSITIONS TAB */}
        {tab === "positions" && (
          <>
            {/* Portfolio Summary */}
            <Card title="Portfolio Summary" accent={totalPnl >= 0 ? "#00ff8844" : "#ff444444"}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"10px", marginBottom:"16px" }}>
                <StatBox label="Total Invested"  value={`$${totalInvested.toFixed(2)}`}  color="white" />
                <StatBox label="Current Value"   value={`$${totalCurrent.toFixed(2)}`}   color={totalPnl >= 0 ? "green" : "red"} />
                <StatBox label="Total P&L"        value={`${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(2)}`} color={totalPnl >= 0 ? "green" : "red"} />
                <StatBox label="Return"           value={`${totalPnlPct >= 0 ? "+" : ""}${totalPnlPct.toFixed(2)}%`} color={totalPnlPct >= 0 ? "green" : "red"} />
              </div>

              {/* Profile breakdown */}
              <div style={{ borderTop:"1px solid #333", paddingTop:"14px" }}>
                <div style={{ color:"#555", fontSize:"11px", marginBottom:"10px", textTransform:"uppercase", letterSpacing:"0.5px" }}>By Profile</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"8px" }}>
                  {["low","medium","high","degen"].map(prof => {
                    const s = profileSummary[prof];
                    if (!s) return (
                      <div key={prof} style={{ background:"#111", padding:"10px", borderRadius:"8px", opacity:0.4 }}>
                        <div style={{ color: profileColors[prof], fontSize:"11px", fontWeight:"700", marginBottom:"4px", textTransform:"uppercase" }}>{prof}</div>
                        <div style={{ color:"#555", fontSize:"12px" }}>No positions</div>
                      </div>
                    );
                    const pct = s.invested > 0 ? (s.pnl / s.invested * 100) : 0;
                    return (
                      <div key={prof} style={{ background:"#111", padding:"10px", borderRadius:"8px", borderLeft:`3px solid ${profileColors[prof]}` }}>
                        <div style={{ color: profileColors[prof], fontSize:"11px", fontWeight:"700", marginBottom:"6px", textTransform:"uppercase" }}>{prof} ({s.count})</div>
                        <div style={{ color:"#fff", fontSize:"13px", fontWeight:"600" }}>${s.invested.toFixed(0)}</div>
                        <div style={{ color: s.pnl >= 0 ? "#00ff88" : "#ff4444", fontSize:"12px", marginTop:"2px" }}>
                          {s.pnl >= 0 ? "+" : ""}${s.pnl.toFixed(2)} ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}%)
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>

            {/* Individual Positions */}
            {positions.length === 0 && <Card><p style={{ color:"#666", textAlign:"center", margin:"20px 0" }}>No open positions.</p></Card>}
            {positions.map(p => (
              <Card key={p.id} accent={p.pnl >= 0 ? "#00ff8844" : "#ff444444"}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                    <span style={{ fontSize:"16px", fontWeight:"700" }}>{p.symbol}</span>
                    <Badge text={p.profile?.toUpperCase()} color={
                      p.profile==="low" ? "blue" : p.profile==="medium" ? "green" :
                      p.profile==="high" ? "yellow" : "red"
                    } />
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:"18px", fontWeight:"700", color: p.pnl >= 0 ? "#00ff88" : "#ff4444" }}>
                      {p.pnl >= 0 ? "+" : ""}${Number(p.pnl||0).toFixed(2)}
                    </div>
                    <div style={{ fontSize:"12px", color: p.pnl >= 0 ? "#00ff8888" : "#ff444488" }}>
                      {p.pnl_pct >= 0 ? "+" : ""}{Number(p.pnl_pct||0).toFixed(2)}%
                    </div>
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"8px", marginBottom:"14px" }}>
                  {[["Entry",   p.entry_price   ? `$${Number(p.entry_price).toLocaleString(undefined,{maximumFractionDigits:4})}`   : "—"],
                    ["Current", p.current_price ? `$${Number(p.current_price).toLocaleString(undefined,{maximumFractionDigits:4})}` : "—"],
                    ["Stop",    p.stop_loss     ? `$${Number(p.stop_loss).toLocaleString(undefined,{maximumFractionDigits:4})}`     : "—"],
                    ["TP2",     p.take_profit   ? `$${Number(p.take_profit).toLocaleString(undefined,{maximumFractionDigits:4})}`   : "—"]
                  ].map(([k,v]) => (
                    <div key={k} style={{ background:"#111", padding:"8px", borderRadius:"6px" }}>
                      <div style={{ color:"#555", fontSize:"10px", marginBottom:"3px" }}>{k}</div>
                      <div style={{ color:"#fff", fontSize:"12px", fontWeight:"600" }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:"8px" }}>
                  <button onClick={() => closePos(p.id, "full")}
                    style={{ padding:"7px 16px", background:"#ff4444", border:"none", borderRadius:"6px", color:"#fff", fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
                    Close Full
                  </button>
                  <button onClick={() => closePos(p.id, "half")}
                    style={{ padding:"7px 16px", background:"#ffaa00", border:"none", borderRadius:"6px", color:"#000", fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
                    Close 50%
                  </button>
                </div>
              </Card>
            ))}
          </>
        )}

        {/* PERFORMANCE TAB */}
        {tab === "performance" && (
          <Card title="Performance Summary">
            {Object.keys(performance).length === 0
              ? <p style={{ color:"#666", textAlign:"center", margin:"20px 0" }}>No performance data yet.</p>
              : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px" }}>
                  {Object.entries(performance).map(([k,v]) => (
                    <StatBox key={k}
                      label={k.replace(/_/g," ")}
                      value={k==="total_pnl" ? `${v >= 0 ? "+" : ""}$${Number(v).toFixed(2)}` : k==="win_rate" ? `${v}%` : String(v)}
                      color={k==="total_pnl" ? (v >= 0 ? "green" : "red") : k==="win_rate" ? (v >= 36.9 ? "green" : "yellow") : "white"}
                    />
                  ))}
                </div>
              )
            }
          </Card>
        )}

        {/* MARKET TAB */}
        {tab === "market" && (
          <Card title="Market Status">
            {Object.keys(market).length === 0
              ? <p style={{ color:"#666", textAlign:"center", margin:"20px 0" }}>No market data yet.</p>
              : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:"10px" }}>
                  {Object.entries(market).map(([k,v]) => (
                    <div key={k} style={{ background:"#111", padding:"12px", borderRadius:"8px" }}>
                      <div style={{ color:"#555", fontSize:"11px", marginBottom:"4px", textTransform:"uppercase" }}>{k.replace(/_/g," ")}</div>
                      <div style={{ color:"#fff", fontSize:"13px", fontWeight:"600" }}>{String(v)}</div>
                    </div>
                  ))}
                </div>
              )
            }
          </Card>
        )}
      </div>
    </div>
  );
}
